import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import { handleCustomResponse } from "../utils/response";
import CardContainer from "../components/ui/CardContainer";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import { staffApi, workflowApi } from "../Apis/Api";
import ErrorAlert from "../components/ui/ErrorAlert";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/* -------------------------- Validation -------------------------- */
const validationSchema = Yup.object().shape({
  from_date: Yup.date().required("From date is required"),
  to_date: Yup.date()
    .required("To date is required")
    .min(Yup.ref("from_date"), "To date must be after from date"),
  branch: Yup.object()
    .shape({ value: Yup.string().nullable(), label: Yup.string() })
    .nullable(),
  location: Yup.object()
    .shape({ value: Yup.string().nullable(), label: Yup.string() })
    .nullable(),
});

/* -------------------------- Main Component ---------------------- */
const RevenueReportsPage = () => {
  /* -------------------------- State -------------------------- */
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);

  const [revenueData, setRevenueData] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [showReport, setShowReport] = useState(false);
 const [currencyCode, setCurrencyCode] = useState("SAR"); // Default fallback
  const [grandTotal, setGrandTotal] = useState({ amount: 0, currency_code: "SAR" });
  const [totalTax, setTotalTax] = useState(0);
  const [netRevenue, setNetRevenue] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const observerRef = useRef(null);
  const tableEndRef = useRef(null);
  const reportRef = useRef(null); // for PDF canvas

  /* ----------------------- React-Hook-Form ----------------------- */
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      from_date: new Date(new Date().setDate(new Date().getDate() - 30))
        .toISOString()
        .split("T")[0],
      to_date: new Date().toISOString().split("T")[0],
      branch: null,
      location: null,
    },
  });

  const fromDate = watch("from_date");
  const toDate = watch("to_date");
  const selectedBranch = watch("branch");
  const selectedLocation = watch("location");

  const totalCompleted = revenueData.reduce((s, r) => s + (r.completed || 0), 0);
  const totalCancelled = revenueData.reduce((s, r) => s + (r.cancelled || 0), 0);

  /* ----------------------- Fetch Branches ----------------------- */
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setError(null);
        const res = await staffApi.getClientBranches({ want_pagination: 0 });
        if (res.success) {
          setBranchList(
            res.data.map(b => ({ value: b.branch_id.toString(), label: b.branch_name }))
          );
        }
      } catch {
        setError(true);
      }
    };
    fetchBranches();
  }, []);

  /* ---------------------- Fetch Locations ----------------------- */
  useEffect(() => {
    const fetchLocations = async () => {
      if (!selectedBranch?.value) {
        setLocationList([]);
        return;
      }
      try {
        setError(null);
        const res = await workflowApi.getBranchLocations(selectedBranch.value);
        if (res.success) {
          setLocationList(
            res.data.map(l => ({ value: l.location_id.toString(), label: l.location_name }))
          );
        } else {
          setLocationList([]);
        }
      } catch {
        setLocationList([]);
        setError(true);
      }
    };
    fetchLocations();
  }, [selectedBranch]);

  /* ---------------------- Flatten API Data ---------------------- */
  const flattenRevenueData = (apiData) => {
    const flat = [];
    if (!apiData || !apiData.branches) return flat;

    apiData.branches.forEach(branch => {
      const { branch_id, branch_name, location_id, location_name, daily_trend = [] } = branch;

      daily_trend.forEach(day => {
        if (!day.date) return;

        const totalRevenue = parseFloat(day.total_revenue || 0);
        const netRevenue = parseFloat(day.net_revenue || 0);
        const tax = parseFloat(day.tax_collected || 0);

        // Extract payment breakdown dynamically
        const breakdown = {};
        if (day.payment_breakdown) {
          Object.keys(day.payment_breakdown).forEach(method => {
            const item = day.payment_breakdown[method];
            breakdown[method] = {
              amount: parseFloat(item.amount || 0),
              percent: parseFloat(item.percent || 0),
            };
          });
        }

        flat.push({
          id: `${branch_id}_${location_id}_${day.date}`,
          date: day.date,
          branch_id,
          branch_name,
          location_id,
          location_name: location_name || "Main Location",
          total_revenue: totalRevenue,
          net_revenue: netRevenue,
          tax_collected: tax,
          completed: day.completed || 0,
          cancelled: day.cancelled || 0,
          avg_ticket: parseFloat(day.avg_ticket || 0),
          peak_hour: day.peak_hour || null,
          breakdown, // { cash: {amount, percent}, card: {...}, upi: {...}, wallet: {...} }
        });
      });
    });

    return flat;
  };

  /* --------------------- Infinite Scroll ---------------------- */
  const lastRecordRef = useCallback(
    node => {
      if (isLoadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) loadMoreData();
      });
      if (node) observerRef.current.observe(node);
    },
    [isLoadingMore, hasMore]
  );

  /* ----------------------- Fetch Revenue ----------------------- */
  const fetchRevenueData = async (
    from,
    to,
    branchId = null,
    locationId = null,
    page = 1,
    reset = true
  ) => {
    try {
      page === 1 ? setIsLoading(true) : setIsLoadingMore(true);
      const fmt = d => {
        const dt = new Date(d);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
          dt.getDate()
        ).padStart(2, "0")}`;
      };
     const res = await staffApi.getRevenueReportV2({
  from_date: fmt(from),
  to_date: fmt(to),
  branch_id: branchId,
  location_id: locationId,
  page,
  per_page: pagination.limit,
});

if (res.success) {
  const apiData = res.data?.data || res.data || {};
  const flat = flattenRevenueData(apiData);
  
  // Set currency code from API
  const currency = apiData.currency_code || "SAR";
  setCurrencyCode(currency);
        if (apiData.grand_total) {
  setGrandTotal({
    amount: parseFloat(apiData.grand_total || 0).toFixed(2),
    currency_code: currency
  });
}
if (apiData.total_tax) {
  setTotalTax(parseFloat(apiData.total_tax || 0).toFixed(2));
}
if (apiData.net_revenue) {
  setNetRevenue(parseFloat(apiData.net_revenue || 0).toFixed(2));
}

        const totalRec = flat.length;
        const totalPages = Math.ceil(totalRec / pagination.limit);

        if (reset || page === 1) setRevenueData(flat);
        else setRevenueData(prev => [...prev, ...flat]);

        setPagination({ page, limit: pagination.limit, total: totalRec, totalPages });
        setHasMore(page < totalPages);
        setShowReport(true);

        if (page === 1)
          handleCustomResponse(
            "success",
            `Revenue report loaded! ${totalRec} record${totalRec !== 1 ? "s" : ""}.`
          );
      } else throw new Error(res.message || "Failed to fetch data");
    } catch (err) {
      handleCustomResponse("error", err.message || "Failed to load report!");
      if (reset) {
        setRevenueData([]);
        setShowReport(false);
      }
    } finally {
      page === 1 ? setIsLoading(false) : setIsLoadingMore(false);
    }
  };

  const loadMoreData = useCallback(() => {
    if (fromDate && toDate && hasMore && !isLoadingMore) {
      fetchRevenueData(
        fromDate,
        toDate,
        selectedBranch?.value,
        selectedLocation?.value,
        pagination.page + 1,
        false
      );
    }
  }, [
    fromDate,
    toDate,
    selectedBranch,
    selectedLocation,
    hasMore,
    isLoadingMore,
    pagination.page,
  ]);

  const onSubmit = data => {
    fetchRevenueData(
      data.from_date,
      data.to_date,
      data.branch?.value,
      data.location?.value,
      1,
      true
    );
  };

  /* ----------------------- Download Modal ----------------------- */
  const openDownloadModal = () => {
    if (!fromDate || !toDate) {
      handleCustomResponse("error", "Date range is required!");
      return;
    }
    setShowDownloadModal(true);
  };

  const closeDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadFormat(null);
  };

  const startDownload = format => {
    setDownloadFormat(format);
    setShowDownloadModal(false);
    setIsGenerating(true);
  };

  /* -------------------------- HTML (EXACT WEB UI) --------------------------- */
  const generateHTML = () => {
    if (!fromDate || !toDate || !revenueData.length) {
      handleCustomResponse("error", "No data to generate report!");
      return;
    }

    const totalCash = revenueData
      .reduce((s, r) => s + (r.breakdown?.cash?.amount || 0), 0)
      .toFixed(2);
    const totalCard = revenueData
      .reduce((s, r) => s + (r.breakdown?.card?.amount || 0), 0)
      .toFixed(2);
    const totalUpi = revenueData
      .reduce((s, r) => s + (r.breakdown?.upi?.amount || 0), 0)
      .toFixed(2);
    const totalWallet = revenueData
      .reduce((s, r) => s + (r.breakdown?.wallet?.amount || 0), 0)
      .toFixed(2);

    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Revenue Report - ${fromDate} to ${toDate}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: #f8f9fa;
      color: #333;
      padding: 20px;
    }
    .container { max-width: 1200px; margin: 0 auto; background: white; padding: 30px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
    .header { text-align: center; margin-bottom: 30px; }
    .header h1 { font-size: 28px; color: #1e293b; margin-bottom: 8px; }
    .header p { font-size: 16px; color: #64748b; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
      font-size: 14px;
    }
    th {
      background: #f1f5f9;
      color: #1e293b;
      font-weight: 600;
      padding: 12px 8px;
      text-align: left;
      border: 1px solid #e2e8f0;
    }
    td {
      padding: 10px 8px;
      border: 1px solid #e2e8f0;
      vertical-align: middle;
    }
    .text-center { text-align: center; }
    .text-end { text-align: right; }
    .text-success { color: #16a34a !important; font-weight: 500; }
    .text-danger { color: #dc2626 !important; font-weight: 500; }
    .total-row {
      background: #e0f2fe !important;
      font-weight: bold;
      font-size: 15px;
    }
    .total-row td { background: #e0f2fe; }
    .footer {
      text-align: center;
      margin-top: 30px;
      color: #94a3b8;
      font-size: 12px;
    }
    @media print {
      body { background: white; padding: 10px; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Revenue Report</h1>
      <p>${fromDate} to ${toDate}${selectedBranch ? " - " + selectedBranch.label : ""}${selectedLocation ? " - " + selectedLocation.label : ""}</p>
    </div>

    <table>
      <thead>
        <tr>
          <th>S.No</th>
          <th>Date</th>
          <th>Branch</th>
          <th>Location</th>
          <th class="text-center">Completed</th>
          <th class="text-center">Cancelled</th>
          <th class="text-end">Cash (${grandTotal.currency_code})</th>
          <th class="text-end">Card (${grandTotal.currency_code})</th>
          <th class="text-end">UPI (${grandTotal.currency_code})</th>
          <th class="text-end">Wallet (${grandTotal.currency_code})</th>
          <th class="text-end">Tax (${grandTotal.currency_code})</th>
          <th class="text-end">Net (${grandTotal.currency_code})</th>
          <th class="text-end">Total (${grandTotal.currency_code})</th>
        </tr>
      </thead>
      <tbody>
        ${revenueData
          .map(
            (r, i) => `
        <tr>
          <td>${(pagination.page - 1) * pagination.limit + i + 1}</td>
          <td>${r.date ? new Date(r.date).toLocaleDateString("en-GB") : "-"}</td>
          <td>${r.branch_name || "-"}</td>
          <td>${r.location_name || "-"}</td>
          <td class="text-center text-success">${r.completed || 0}</td>
          <td class="text-center text-danger">${r.cancelled || 0}</td>
          <td class="text-end">
            ${parseFloat(r.breakdown?.cash?.amount || 0).toFixed(2)} 
            <small style="color:#64748b;">(${r.breakdown?.cash?.percent || 0}%)</small>
          </td>
          <td class="text-end">
            ${parseFloat(r.breakdown?.card?.amount || 0).toFixed(2)} 
            <small style="color:#64748b;">(${r.breakdown?.card?.percent || 0}%)</small>
          </td>
          <td class="text-end">
            ${parseFloat(r.breakdown?.upi?.amount || 0).toFixed(2)} 
            <small style="color:#64748b;">(${r.breakdown?.upi?.percent || 0}%)</small>
          </td>
          <td class="text-end">
            ${parseFloat(r.breakdown?.wallet?.amount || 0).toFixed(2)} 
            <small style="color:#64748b;">(${r.breakdown?.wallet?.percent || 0}%)</small>
          </td>
          <td class="text-end">${parseFloat(r.tax_collected || 0).toFixed(2)}</td>
          <td class="text-end">${parseFloat(r.net_revenue || 0).toFixed(2)}</td>
          <td class="text-end">${parseFloat(r.total_revenue || 0).toFixed(2)}</td>
        </tr>
        `
          )
          .join("")}
        <tr class="total-row">
          <td colspan="4" class="text-end">Total:</td>
          <td class="text-center text-success">${totalCompleted}</td>
          <td class="text-center text-danger">${totalCancelled}</td>
          <td class="text-end">${totalCash} ${grandTotal.currency_code}</td>
          <td class="text-end">${totalCard} ${grandTotal.currency_code}</td>
          <td class="text-end">${totalUpi} ${grandTotal.currency_code}</td>
          <td class="text-end">${totalWallet} ${grandTotal.currency_code}</td>
          <td class="text-end">${totalTax} ${grandTotal.currency_code}</td>
          <td class="text-end">${netRevenue} ${grandTotal.currency_code}</td>
          <td class="text-end">${grandTotal.amount} ${grandTotal.currency_code}</td>
        </tr>
      </tbody>
    </table>

    <div class="footer">
      Generated on: ${new Date().toLocaleString()}
    </div>
  </div>
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `revenue_report_${fromDate}_to_${toDate}${
      selectedBranch ? "_" + selectedBranch.value : ""
    }${selectedLocation ? "_" + selectedLocation.value : ""}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    handleCustomResponse("success", "HTML report downloaded successfully!");
  };

  /* -------------------------- Excel -------------------------- */
  const generateExcel = () => {
    const rows = [
      ["Revenue Report"],
      [
        `${fromDate} to ${toDate}${
          selectedBranch ? " - " + selectedBranch.label : ""
        }${selectedLocation ? " - " + selectedLocation.label : ""}`,
      ],
      [],
      [
        "#",
        "Date",
        "Branch",
        "Location",
        "Completed",
        "Cancelled",
        "Cash",
        "Card",
        "UPI",
        "Wallet",
        "Tax",
        "Net Revenue",
        "Total Revenue",
      ],
    ];

    revenueData.forEach((r, i) => {
      const cashAmt = parseFloat(r.breakdown?.cash?.amount ?? 0).toFixed(2);
      const cashPct = r.breakdown?.cash?.percent ?? 0;
      const cardAmt = parseFloat(r.breakdown?.card?.amount ?? 0).toFixed(2);
      const cardPct = r.breakdown?.card?.percent ?? 0;
      const upiAmt = parseFloat(r.breakdown?.upi?.amount ?? 0).toFixed(2);
      const upiPct = r.breakdown?.upi?.percent ?? 0;
      const walletAmt = parseFloat(r.breakdown?.wallet?.amount ?? 0).toFixed(2);
      const walletPct = r.breakdown?.wallet?.percent ?? 0;

      rows.push([
        i + 1,
        r.date ? new Date(r.date).toLocaleDateString("en-GB") : "-",
        r.branch_name || "-",
        r.location_name || "-",
        r.completed || 0,
        r.cancelled || 0,
        `${cashAmt} (${cashPct}%)`,
        `${cardAmt} (${cardPct}%)`,
        `${upiAmt} (${upiPct}%)`,
        `${walletAmt} (${walletPct}%)`,
        parseFloat(r.tax_collected || 0).toFixed(2),
        parseFloat(r.net_revenue || 0).toFixed(2),
        parseFloat(r.total_revenue || 0).toFixed(2),
      ]);
    });

    const totalCash = revenueData
      .reduce((s, r) => s + (parseFloat(r.breakdown?.cash?.amount) || 0), 0)
      .toFixed(2);
    const totalCard = revenueData
      .reduce((s, r) => s + (parseFloat(r.breakdown?.card?.amount) || 0), 0)
      .toFixed(2);
    const totalUpi = revenueData
      .reduce((s, r) => s + (parseFloat(r.breakdown?.upi?.amount) || 0), 0)
      .toFixed(2);
    const totalWallet = revenueData
      .reduce((s, r) => s + (parseFloat(r.breakdown?.wallet?.amount) || 0), 0)
      .toFixed(2);

    rows.push([
      "Total:",
      "",
      "",
      "",
      totalCompleted,
      totalCancelled,
      `${totalCash} ${grandTotal.currency_code}`,
      `${totalCard} ${grandTotal.currency_code}`,
      `${totalUpi} ${grandTotal.currency_code}`,
      `${totalWallet} ${grandTotal.currency_code}`,
      `${totalTax} ${grandTotal.currency_code}`,
      `${netRevenue} ${grandTotal.currency_code}`,
      `${grandTotal.amount} ${grandTotal.currency_code}`,
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Revenue");

    // Bold total row
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: rows.length - 1, c: C });
      if (ws[addr]) ws[addr].s = { font: { bold: true } };
    }

    const fileName = `revenue_report_${fromDate}_to_${toDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
    handleCustomResponse("success", "Excel downloaded successfully!");
  };

  /* -------------------------- PDF -------------------------- */
  const generatePDF = async () => {
    if (!reportRef.current) return;
    // Save current zoom and theme
    const originalZoom = document.documentElement.style.zoom;
    const originalTransform = document.documentElement.style.transform;
    const originalWidth = document.documentElement.style.width;
    const originalHeight = document.documentElement.style.height;
    const currentTheme = document.documentElement.getAttribute('data-theme');
   
    try {
      // Temporarily reset zoom to 100% and force light theme for PDF
      document.documentElement.style.zoom = "100%";
      document.documentElement.style.transform = "";
      document.documentElement.style.width = "";
      document.documentElement.style.height = "";
      
      // Force light theme for PDF generation
      if (currentTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'light');
      }
     
      // Wait for theme change to apply
      await new Promise(resolve => setTimeout(resolve, 200));
     
      const canvas = await html2canvas(reportRef.current, { 
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      const fileName = `revenue_report_${fromDate}_to_${toDate}${
        selectedBranch ? "_" + selectedBranch.value : ""
      }${selectedLocation ? "_" + selectedLocation.value : ""}.pdf`;
      pdf.save(fileName);
      handleCustomResponse("success", "PDF report downloaded successfully!");
    } finally {
      // Restore original zoom and theme
      document.documentElement.style.zoom = originalZoom;
      document.documentElement.style.transform = originalTransform;
      document.documentElement.style.width = originalWidth;
      document.documentElement.style.height = originalHeight;
      
      // Restore original theme
      if (currentTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
      }
    }
  };

  /* ----------------------- Run Download ------------------- */
  useEffect(() => {
    if (!isGenerating || !downloadFormat) return;

    const run = async () => {
      try {
        if (downloadFormat === "html") {
          generateHTML();
        } else if (downloadFormat === "excel") {
          generateExcel();
        } else if (downloadFormat === "pdf") {
          await generatePDF();
        }
      } catch (err) {
        console.error("Download error:", err);
        handleCustomResponse("error", "Failed to generate file. Please try again.");
      } finally {
        setIsGenerating(false);
        setDownloadFormat(null);
      }
    };

    run();
  }, [isGenerating, downloadFormat]);

  /* -------------------------- Table -------------------------- */
  const renderTable = () => {
    if (!revenueData.length) {
      return (
        <TableDataNoAvailable
          message={
            fromDate || toDate || selectedBranch || selectedLocation
              ? "No revenue data matches your selection."
              : "No revenue data available."
          }
        />
      );
    }

    const totalCash = revenueData.reduce((s, r) => s + (r.breakdown?.cash?.amount || 0), 0).toFixed(2);
    const totalCard = revenueData.reduce((s, r) => s + (r.breakdown?.card?.amount || 0), 0).toFixed(2);
    const totalUpi = revenueData.reduce((s, r) => s + (r.breakdown?.upi?.amount || 0), 0).toFixed(2);
    const totalWallet = revenueData.reduce((s, r) => s + (r.breakdown?.wallet?.amount || 0), 0).toFixed(2);

    return (
      <div
        className="position-relative"
        style={{ opacity: isLoading ? 0.4 : 1, pointerEvents: isLoading ? "none" : "auto" }}
      >
        {/* Hidden printable version for PDF */}
        <div style={{ position: "absolute", left: "-9999px" }} ref={reportRef}>
          <div style={{ padding: "30px", fontFamily: "'Segoe UI', sans-serif", background: "#fff" }}>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h1 style={{ fontSize: "28px", color: "#1e293b" }}>Revenue Report</h1>
              <p style={{ fontSize: "16px", color: "#64748b" }}>
                {fromDate} to {toDate}
                {selectedBranch && ` - ${selectedBranch.label}`}
                {selectedLocation && ` - ${selectedLocation.label}`}
              </p>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>S.No</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Date</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Branch</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "left" }}>Location</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "center" }}>Completed</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "center" }}>Cancelled</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>Cash ({grandTotal.currency_code})</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>Card ({grandTotal.currency_code})</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>UPI ({grandTotal.currency_code})</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>Wallet ({grandTotal.currency_code})</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>Tax ({grandTotal.currency_code})</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>Net ({grandTotal.currency_code})</th>
                  <th style={{ background: "#f1f5f9", padding: "12px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>Total ({grandTotal.currency_code})</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.date ? new Date(r.date).toLocaleDateString("en-GB") : "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.branch_name || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0" }}>{r.location_name || "-"}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "center", color: "#16a34a" }}>{r.completed || 0}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "center", color: "#dc2626" }}>{r.cancelled || 0}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>
                      {parseFloat(r.breakdown?.cash?.amount || 0).toFixed(2)} 
                      <small style={{ color: "#64748b" }}>({r.breakdown?.cash?.percent || 0}%)</small>
                    </td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>
                      {parseFloat(r.breakdown?.card?.amount || 0).toFixed(2)} 
                      <small style={{ color: "#64748b" }}>({r.breakdown?.card?.percent || 0}%)</small>
                    </td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>
                      {parseFloat(r.breakdown?.upi?.amount || 0).toFixed(2)} 
                      <small style={{ color: "#64748b" }}>({r.breakdown?.upi?.percent || 0}%)</small>
                    </td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>
                      {parseFloat(r.breakdown?.wallet?.amount || 0).toFixed(2)} 
                      <small style={{ color: "#64748b" }}>({r.breakdown?.wallet?.percent || 0}%)</small>
                    </td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>{parseFloat(r.tax_collected || 0).toFixed(2)}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>{parseFloat(r.net_revenue || 0).toFixed(2)}</td>
                    <td style={{ padding: "10px 8px", border: "1px solid #e2e8f0", textAlign: "right" }}>{parseFloat(r.total_revenue || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ background: "#e0f2fe", fontWeight: "bold", fontSize: "15px" }}>
                  <td colSpan={4} style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>Total:</td>
                  <td style={{ textAlign: "center", padding: "10px 8px", border: "1px solid #e2e8f0", color: "#16a34a" }}>{totalCompleted}</td>
                  <td style={{ textAlign: "center", padding: "10px 8px", border: "1px solid #e2e8f0", color: "#dc2626" }}>{totalCancelled}</td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {totalCash} {grandTotal.currency_code}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {totalCard} {grandTotal.currency_code}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {totalUpi} {grandTotal.currency_code}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {totalWallet} {grandTotal.currency_code}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {totalTax} {grandTotal.currency_code}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {netRevenue} {grandTotal.currency_code}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px 8px", border: "1px solid #e2e8f0" }}>
                    {grandTotal.amount} {grandTotal.currency_code}
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ textAlign: "center", marginTop: "30px", color: "#94a3b8", fontSize: "12px" }}>
              Generated on: {new Date().toLocaleString()}
            </div>
          </div>
        </div>

        <div className="table-responsive scroll-sm">
          <table className="table bordered-table table-sm mb-0">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>
                <th>Branch</th>
                <th>Location</th>
                <th className="text-center">Completed</th>
                <th className="text-center">Cancelled</th>
                <th className="text-end">Cash ({grandTotal.currency_code})</th>
                <th className="text-end">Card ({grandTotal.currency_code})</th>
                <th className="text-end">UPI ({grandTotal.currency_code})</th>
                <th className="text-end">Wallet ({grandTotal.currency_code})</th>
                <th className="text-end">Tax Collected ({grandTotal.currency_code})</th>
                <th className="text-end">Net Revenue ({grandTotal.currency_code})</th>
                <th className="text-end">Total ({grandTotal.currency_code})</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.map((r, i) => (
                <tr
                  key={r.id || `${i}-${r.date}`}
                  ref={i === revenueData.length - 1 ? lastRecordRef : null}
                >
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td>{r.date ? new Date(r.date).toLocaleDateString("en-GB") : "-"}</td>
                  <td>{r.branch_name || "-"}</td>
                  <td>{r.location_name || "-"}</td>
                  <td className="text-center text-success">{r.completed || 0}</td>
                  <td className="text-center text-danger">{r.cancelled || 0}</td>
                  <td className="text-end">
                    {r.breakdown?.cash?.amount
                      ? parseFloat(r.breakdown.cash.amount).toFixed(2)
                      : "0.00"}{" "}
                    ({r.breakdown?.cash?.percent ?? 0}%)
                  </td>
                  <td className="text-end">
                    {r.breakdown?.card?.amount
                      ? parseFloat(r.breakdown.card.amount).toFixed(2)
                      : "0.00"}{" "}
                    ({r.breakdown?.card?.percent ?? 0}%)
                  </td>
                  <td className="text-end">
                    {r.breakdown?.upi?.amount
                      ? parseFloat(r.breakdown.upi.amount).toFixed(2)
                      : "0.00"}{" "}
                    ({r.breakdown?.upi?.percent ?? 0}%)
                  </td>
                  <td className="text-end">
                    {r.breakdown?.wallet?.amount
                      ? parseFloat(r.breakdown.wallet.amount).toFixed(2)
                      : "0.00"}{" "}
                    ({r.breakdown?.wallet?.percent ?? 0}%)
                  </td>
                  <td className="text-end">
                    {r.tax_collected ? parseFloat(r.tax_collected).toFixed(2) : "0.00"}
                  </td>
                  <td className="text-end">
                    {r.net_revenue ? parseFloat(r.net_revenue).toFixed(2) : "0.00"}
                  </td>
                  <td className="text-end">
                    {r.total_revenue ? parseFloat(r.total_revenue).toFixed(2) : "0.00"}
                  </td>
                </tr>
              ))}
              <tr className="total-row bg-light">
                <td colSpan={4} className="text-end fw-bold">
                  Total:
                </td>
                <td className="text-center fw-bold text-success">{totalCompleted}</td>
                <td className="text-center fw-bold text-danger">{totalCancelled}</td>
                <td className="text-end fw-bold">
                  {totalCash}
                </td>
                <td className="text-end fw-bold">
                  {totalCard}
                </td>
                <td className="text-end fw-bold">
                  {totalUpi}
                </td>
                <td className="text-end fw-bold">
                  {totalWallet}
                </td>
                <td className="text-end fw-bold">
                  {totalTax}
                </td>
                <td className="text-end fw-bold">
                  {netRevenue}
                </td>
                <td className="text-end fw-bold">
                  {grandTotal.amount} {grandTotal.currency_code}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {isLoadingMore && (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading more...</span>
            </div>
            <span className="text-secondary-light ms-2">Loading more records...</span>
          </div>
        )}

        {!hasMore && revenueData.length > 0 && (
          <div className="text-center py-3">
            <span className="text-secondary-light">No more records to load</span>
          </div>
        )}
      </div>
    );
  };

  /* -------------------------- Cleanup -------------------------- */
  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  /* --------------------------- Render -------------------------- */
  if (error) return <ErrorAlert error={error} />;

  return (
    <>
      <Breadcrumb title="Revenue Reports" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-16 p-sm-24">
          <form onSubmit={handleSubmit(onSubmit)} className="mb-24">
            <div className="row g-3 g-md-4 align-items-end">
              {/* From Date */}
              <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label fw-medium text-neutral-900">
                  From Date *
                </label>
                <Controller
                  name="from_date"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className={`form-control radius-8 ${errors.from_date ? "is-invalid" : ""}`}
                    />
                  )}
                />
                {errors.from_date && (
                  <div className="invalid-feedback">{errors.from_date.message}</div>
                )}
              </div>

              {/* To Date */}
              <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label fw-medium text-neutral-900">
                  To Date *
                </label>
                <Controller
                  name="to_date"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className={`form-control radius-8 ${errors.to_date ? "is-invalid" : ""}`}
                    />
                  )}
                />
                {errors.to_date && (
                  <div className="invalid-feedback">{errors.to_date.message}</div>
                )}
              </div>

              {/* Branch */}
              <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label fw-medium text-neutral-900">Branch</label>
                <Controller
                  name="branch"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={branchList}
                      placeholder="Select Branch"
                      isClearable
                      noOptionsMessage={() => "No branches available"}
                    />
                  )}
                />
              </div>

              {/* Location */}
              <div className="col-12 col-sm-6 col-md-6 col-lg-2">
                <label className="form-label fw-medium text-neutral-900">Location</label>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      options={locationList}
                      placeholder="Select Location"
                      isClearable
                      isDisabled={!selectedBranch}
                      noOptionsMessage={() =>
                        selectedBranch ? "No locations available" : "Select a branch first"
                      }
                    />
                  )}
                />
              </div>

              {/* Buttons */}
              <div className="col-12 col-lg-4">
                <div className="d-flex flex-column flex-sm-row gap-2 gap-lg-3">
                  <button
                    type="submit"
                    disabled={isLoading || !isValid}
                    className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center justify-content-center gap-2 flex-fill"
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="d-none d-sm-inline">Loading...</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:eye" width="18" />
                        <span>View Report</span>
                      </>
                    )}
                  </button>

                  {showReport && (
                    <button
                      type="button"
                      onClick={openDownloadModal}
                      disabled={isGenerating}
                      className="btn btn-success-600 radius-8 px-20 py-11 d-flex align-items-center justify-content-center gap-2 flex-fill"
                    >
                      {isGenerating ? (
                        <>
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Generating...</span>
                          </div>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:download" width="18" />
                          <span>Download</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>

          {showReport && (
            <CardContainer className="card border-0 shadow-sm">
              <div className="card-header border-bottom bg-base py-16 px-20 px-md-24 d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
                <div className="flex-grow-1">
                  <h6 className="text-lg mb-1 fw-semibold text-neutral-900">
                    Revenue Report
                  </h6>
                  <p className="text-sm text-neutral-600 mb-0">
                    {fromDate} to {toDate}
                    {selectedBranch && ` - ${selectedBranch.label}`}
                    {selectedLocation && ` - ${selectedLocation.label}`}
                  </p>
                </div>
                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3">
                  {pagination.total > 0 && (
                    <span className="text-sm text-neutral-600 order-2 order-sm-1">
                      Showing {revenueData.length} of {pagination.total} records
                      {totalCompleted > 0 && (
                        <>
                          {" "}
                          • Completed: <span className="text-success">{totalCompleted}</span> |
                          Cancelled: <span className="text-danger">{totalCancelled}</span>
                        </>
                      )}
                    </span>
                  )}
                  {revenueData.length > 0 && (
                    <span className="badge bg-primary-100 text-primary-700 px-12 py-6 fw-medium order-1 order-sm-2">
                      Total: {grandTotal.amount} {grandTotal.currency_code}
                    </span>
                  )}
                </div>
              </div>

              <div className="card-body p-0">
                
                
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-neutral-500 mt-2 mb-0">Loading revenue data...</p>
                  </div>
                ) : (
                  renderTable()
                )}
              </div>
            </CardContainer>
          )}
        </div>
      </div>

      {/* --------------------- DOWNLOAD MODAL (Improved UI) --------------------- */}
      <div
        className={`modal fade ${showDownloadModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDownloadModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">
                {/* <Icon icon="mdi:file-download" className="me-2" /> */}
                Download Report
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeDownloadModal}
                aria-label="Close"
              />
            </div>
            <div className="modal-body pt-2">
              <p className="text-neutral-700 mb-3">
                Select the file format you want to generate:
              </p>
              <div className="d-grid gap-2">
                {/* <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2"
                  onClick={() => startDownload("html")}
                  disabled={isGenerating}
                >
                  <Icon icon="mdi:language-html5" width="20" />
                  <span>HTML Report</span>
                  {isGenerating && downloadFormat === "html" && (
                    <div className="spinner-border spinner-border-sm ms-auto" role="status" />
                  )}
                </button> */}

                <button
                  type="button"
                  className="btn btn-outline-success d-flex align-items-center justify-content-center gap-2"
                  onClick={() => startDownload("excel")}
                  disabled={isGenerating}
                >
                  <Icon icon="mdi:microsoft-excel" width="20" />
                  <span>Excel (.xlsx)</span>
                  {isGenerating && downloadFormat === "excel" && (
                    <div className="spinner-border spinner-border-sm ms-auto" role="status" />
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger d-flex align-items-center justify-content-center gap-2"
                  onClick={() => startDownload("pdf")}
                  disabled={isGenerating}
                >
                  <Icon icon="mdi:file-pdf-box" width="20" />
                  <span>PDF Document</span>
                  {isGenerating && downloadFormat === "pdf" && (
                    <div className="spinner-border spinner-border-sm ms-auto" role="status" />
                  )}
                </button>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={closeDownloadModal}
                disabled={isGenerating}
              >
                {/* <Icon icon="mdi:close" width="18" /> */}
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RevenueReportsPage;